import React, { useLayoutEffect, useMemo, useState } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import {
  formatDateWithDay,
  getAmChartsLocale,
  processDataByMonth,
  shouldShowYear,
} from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";
import GroupByDropdown from "./GroupByDropdown/GroupByDropdown";

const OrderCountByPeriodExpeditor = ({ item }) => {
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState("day");

  const processedData = useMemo(() => {
    const fallbackDate = new Date();

    const safeItem =
      Array.isArray(item) && item.length > 0
        ? item
        : [
            {
              createdAt: fallbackDate.toISOString(),
              completed: 0,
            },
          ];

    const transformedData = safeItem.map((dataItem) => ({
      date: new Date(dataItem.createdAt).toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
      completed: dataItem.completed || 0,
      createdAt: dataItem.createdAt,
    }));

    if (groupBy === "day") {
      const showYear = shouldShowYear(transformedData);

      return transformedData
        .map((dataItem) => {
          const [day, month, year] = dataItem.date.split(".");
          const isoDate = new Date(year, month - 1, day);

          return {
            ...dataItem,
            month: formatDateWithDay(isoDate, currentLanguage, showYear),
            tooltipText: formatDateWithDay(isoDate, currentLanguage, showYear).replace(/\n/g, " "),
            isoDate,
          };
        })
        .sort((a, b) => a.isoDate - b.isoDate);
    } else {
      const numericFields = ["completed"];
      return processDataByMonth(transformedData, currentLanguage, numericFields);
    }
  }, [item, currentLanguage, groupBy]);

  useLayoutEffect(() => {
    const root = am5.Root.new("order-count-by-period-expeditor");
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo?.dispose();

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelY: "zoomX",
        panX: true,
        panY: false,
        zoomX: true,
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
      }),
    );
    cursor.lineY.set("visible", false);

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "category",
        groupData: true,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: groupBy === "day" ? 60 : 40,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
        tooltip: am5.Tooltip.new(root, {
          labelText: "{category}",
        }),
      }),
    );

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    if (groupBy === "day") {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 70,
        oversizedBehavior: "wrap",
      });
    } else {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 50,
        oversizedBehavior: "wrap",
      });
    }

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        min: 0,
      }),
    );

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    let chartData = [];

    if (groupBy === "day") {
      const groupedByDate = {};

      processedData.forEach((item) => {
        const dateKey = item.month;
        if (!groupedByDate[dateKey]) {
          groupedByDate[dateKey] = {
            category: dateKey,
            tooltipText: item.tooltipText,
            completed: 0,
            isoDate: item.isoDate,
          };
        }
        groupedByDate[dateKey].completed += item.completed;
      });

      chartData = Object.values(groupedByDate).sort((a, b) => a.isoDate - b.isoDate);
    } else {
      chartData = processedData.map((item) => ({
        category: item.month || item.date,
        completed: item.completed,
        tooltipText: item.month || item.date,
      }));
    }

    xAxis.data.setAll(
      chartData.map((item) => ({
        category: item.category,
      })),
    );

    const completedSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: t("analytics.fulfilled"),
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "completed",
        categoryXField: "category",
        fill: am5.color("#4E7CFF"),
        stroke: am5.color("#4E7CFF"),
        tooltip: am5.Tooltip.new(root, {
          labelText: `${t("analytics.fulfilled")}: {completed}`,
        }),
      }),
    );

    completedSeries.strokes.template.setAll({
      strokeWidth: 3,
    });

    completedSeries.bullets.push(function (root, series, dataItem) {
      const circle = am5.Circle.new(root, {
        radius: 4,
        fill: am5.color("#4E7CFF"),
        strokeWidth: 2,
      });

      return am5.Bullet.new(root, {
        sprite: circle,
      });
    });

    completedSeries.bullets.push(function (root, series, dataItem) {
      return am5.Bullet.new(root, {
        sprite: am5.Label.new(root, {
          text: "{completed}",
          fill: am5.color(0x000000),
          centerY: am5.p100,
          centerX: am5.p50,
          populateText: true,
          fontSize: 11,
          fontWeight: "500",
        }),
      });
    });

    completedSeries.data.setAll(chartData);

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
      }),
    );
    legend.data.setAll(chart.series.values);

    return () => root.dispose();
  }, [processedData, isMobileView, currentLanguage, groupBy, t]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.orderCountByPeriod")}</span>
        <GroupByDropdown groupBy={groupBy} setGroupBy={setGroupBy} />
      </div>
      <div className={style.area}>
        <div id="order-count-by-period-expeditor" className={style.area_chart}></div>
      </div>
    </div>
  );
};

export default OrderCountByPeriodExpeditor;
